import { Signal, computed, effect, inject } from '@angular/core';
import { RightsService } from '@jms/utils/util.permissions';
import { Command } from '@workbuddy/ui/model';
import { ActivityEntityName, ContactEntity, ContactType, EntityQueue, EntityQueueFieldSource, EntityQueueTableConfig, EntityQueueTableField, EntityQueueType, EntityView, EntityViewDisplay, EntityViewSortDirection, EntityViewVisibility, MetadataEntityData, MetadataEntityFieldDomain } from '@jms/model';
import { AdvancedFilterPanelComponent, SettingsPanelBuilder, SettingsPanelSelectOption, SettingsPanelValidationResult, buildDisplayPanel, displayPanelSummary, filterSummary, splitPinnedSelection, toFieldOptions, toFieldOptionsWithNone } from '@workbuddy/ui/v2/blocks/settings.panel';
import { FilterFieldProvider } from '@workbuddy/ui/v2/blocks/filter';
import { EntityFilterFieldProvider } from '@workbuddy/ui/v2/blocks/entity.board/providers';
import { ViewSettingsValue } from '@workbuddy/ui/v2/blocks/entity.board/components/view.settings';
import { QueueFieldOption } from '../../queue.field.options';
import { resolveQueueAppearance } from '../../queue.appearance';
import { nameRequired } from '../validators/name.required.validator';
import { injectQueueNameUniqueChecker, nameUnique } from '../validators/name.unique.validator';
import { QueueAdapterDeps, QueueStepAdapter } from './queue.step.adapter';
import { allowedQueueVisibilities } from '../../queue.permissions.policy';
import { QUEUE_SHARED_WITH_TEAM_GROUP, createQueueSharedWithSummary } from '../../queue.shared.with.summary';

export interface EntityAdapterConfig {
  entity: ActivityEntityName;
  entityLabel: string;
  resolveColumnOptions: (metadataMap: Record<string, MetadataEntityData>) => QueueFieldOption[];
  defaultTable: () => EntityQueueTableConfig;
}

export function createEntityAdapter(deps: QueueAdapterDeps, config: EntityAdapterConfig): QueueStepAdapter {

  const isQueueNameUnique = injectQueueNameUniqueChecker();
  const rights = inject(RightsService);

  const entityFilterProvider = new EntityFilterFieldProvider();

  const $metadataMap = computed(() => deps.$config()?.metadataMap ?? {});
  const $columnOptions = computed(() => config.resolveColumnOptions($metadataMap()));

  // One Columns picker shows, hides and pins; a pinned row is a fixed column, so no column can land in both lists.
  const $tableColumnOptions = computed(() => toFieldOptions($columnOptions()));
  const $sortOptions: Signal<SettingsPanelSelectOption[]> = computed(() => toFieldOptionsWithNone($columnOptions().filter(f => f.sortable)));

  effect(() => {

    const metadataMap = $metadataMap();
    const entityMeta = metadataMap[config.entity];
    if (!entityMeta) return;
    const domain = new MetadataEntityFieldDomain(entityMeta);
    const fields = Object.values(domain.uiFields).filter((f: any) => f.filterable);
    entityFilterProvider.configureFields(config.entity, fields);
  });

  const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

  const toTableFields = (ids: string[], fields: QueueFieldOption[]) =>
    (ids ?? []).map(id => {

      const f = fields.find(f => f.path === id);
      return { ref: { source: EntityQueueFieldSource.Entity, path: id }, name: f?.displayName ?? id, visible: true };
    });

  const buildSharedWith = (value: unknown): ContactEntity[] => {

    if (!Array.isArray(value)) return [];

    return value.map(entry => {

      if (typeof entry === 'string') return { _id: entry } as ContactEntity;
      if (!entry || typeof entry !== 'object') return undefined;

      const contact = entry as Partial<ContactEntity>;
      if (!contact._id) return undefined;
      return { _id: contact._id, name: contact.name, type: contact.type } as ContactEntity;
    }).filter((entry): entry is ContactEntity => !!entry);
  };

  const resolveSortRef = (sortBy: string | undefined) => {

    if (!sortBy || sortBy === 'none') return null;
    return { source: EntityQueueFieldSource.Entity, path: sortBy };
  };

  const resolveInitialTable = (model: Partial<EntityQueue>, isEdit: boolean): EntityQueueTableConfig => {

    if (isEdit) return model.table ?? config.defaultTable();
    return model.table?.fixed?.length || model.table?.other?.length ? model.table : config.defaultTable();
  };

  const buildValue = (formValues: Record<string, unknown>, model: Partial<EntityQueue>): Partial<EntityQueue> => {

    const values = formValues as Record<string, any>;
    const fields = $columnOptions();
    const columns = splitPinnedSelection(values.list?.columns);
    const standardColumns = columns.unpinned as string[];
    const fixedColumns = columns.pinned as string[];

    const queue: Partial<EntityQueue> = {
      name: values.name ?? model.name,
      type: EntityQueueType.Entity,
      visibility: values.visibility,
      sharedWith: values.visibility === EntityViewVisibility.Shared ? buildSharedWith(values.sharedWith) : [],
      entity: config.entity,
      entityFilter: values.entityFilter ?? undefined,
      appearance: { tone: values.tone, icon: values.icon || undefined },
    };

    queue.table = {
      display: EntityViewDisplay.Table,
      other: toTableFields(standardColumns, fields),
      fixed: toTableFields(fixedColumns, fields),
      sortBy: resolveSortRef(values.list?.sortBy),
      sortOrder: values.list?.sortOrder ?? EntityViewSortDirection.Asc,
    };

    return queue;
  };

  const buildPreviewValue = (formValues: Record<string, unknown>, model: Partial<EntityQueue>): ViewSettingsValue => {

    const queue = buildValue(formValues, model);
    const toViewFields = (fields: EntityQueueTableField[] = []) =>
      fields.map(field => ({ _id: field.ref.path, name: field.name ?? field.ref.path, visible: field.visible ?? true, systemHidden: false }));

    return {
      view: {
        display: EntityViewDisplay.Table,
        table: {
          other: toViewFields(queue.table?.other),
          fixed: toViewFields(queue.table?.fixed),
        },
        sort: queue.table?.sortBy
          ? { _id: queue.table.sortBy.path, direction: queue.table.sortOrder ?? EntityViewSortDirection.Asc }
          : null,
      } as Partial<EntityView>,
      pageSize: queue.table?.pageSize ?? 50,
      request: { preview: { queue } },
    };
  };

  const validate = (formValues: Record<string, unknown>): SettingsPanelValidationResult => {

    return [...(nameRequired(formValues) ?? [])];
  };

  return {
    buildValue,
    build: (ctx, model, wizard) => {

      const isEdit = !!model._id;
      const table = resolveInitialTable(model, isEdit);

      const visibilityCommands = allowedQueueVisibilities(isEdit, deps.$config()?.isOwner ?? true, right => rights.hasRights(right)).map(visibility => new Command({ _id: visibility, label: visibility }));

      const selectedVisibility = visibilityCommands.some(c => c._id === model.visibility)
        ? model.visibility
        : visibilityCommands[0]?._id;

      const defaultAppearance = resolveQueueAppearance({ ...model, type: EntityQueueType.Entity });

      const sharedWithSummary = createQueueSharedWithSummary(model.sharedWith ?? []);

      const builder = new SettingsPanelBuilder(isEdit ? 'Edit Queue' : 'Create Queue')
        .input('queueType', 'Queue Type', { value: 'Entity', disabled: () => true })
        .input('entity', 'Entity', { value: config.entityLabel, disabled: () => true })
        .divider()
        .input('name', 'Name', {
          placeholder: 'Queue name',
          value: model.name ?? '',
          autofocus: isEdit ? 'selectAll' : false,
          errorMessage: { required: 'Queue name is required', unique: 'Queue name already taken' },
        })
        .menu('appearance', 'Appearance', {
          displayValue: ({ form }) => computed(() => {
            const v = form() as Record<string, any>;
            return { icon: v.icon || defaultAppearance.icon, tone: v.tone || defaultAppearance.tone };
          }),
        }, m => {
          m.iconPicker('icon', 'Icon', { value: defaultAppearance.icon });
          m.tonePicker('tone', 'Color', { value: defaultAppearance.tone });
        })
        .tabs('visibility', 'View Access', { commands: visibilityCommands, selected: selectedVisibility })
        .entityPicker('sharedWith', 'Shared With', {
          entity: [ActivityEntityName.ContactEmployee, ActivityEntityName.Team],
          mode: 'multi',
          selected: model.sharedWith?.map(e => e._id) ?? [],
          selectedOptions: (model.sharedWith ?? []).map(entry => ({
            id: entry._id,
            label: entry.name ?? entry._id,
            avatar: entry.name,
            group: entry.type === ContactType.Team ? QUEUE_SHARED_WITH_TEAM_GROUP : 'Employees',
            data: entry,
          })),
          groupLabel: (entity) => entity === ActivityEntityName.Team ? QUEUE_SHARED_WITH_TEAM_GROUP : 'Employees',
          onSelect: option => { sharedWithSummary.remember(option); return undefined; },
          hidden: (values) => values.visibility !== EntityViewVisibility.Shared,
          errorMessage: { required: 'Select at least one member' },
          validator: ({ value, form }) => form.visibility === EntityViewVisibility.Shared && !asArray(value).length ? { required: true } : null,
          displayValue: ({ field }) => computed(() => sharedWithSummary.describe(field())),
        })
        .divider()
        .navigate('entityFilter', `${config.entityLabel} Filter`, {
          component: AdvancedFilterPanelComponent,
          inputs: { fieldKey: 'entityFilter', autoApply: true },
          initialValue: model.entityFilter ?? {},
          displayValue: filterSummary(),
          navigateOptions: { providers: [{ provide: FilterFieldProvider, useValue: entityFilterProvider }] },
        })
        .divider()
        .menu('displaySettings', 'Display', { displayValue: displayPanelSummary([EntityViewDisplay.Table]) }, m => {
          buildDisplayPanel(m, {
            types: [EntityViewDisplay.Table],
            selected: EntityViewDisplay.Table,
            sortableOptions: $sortOptions,
            table: {
              columnOptions: $tableColumnOptions,
              selectedColumns: table.other?.map(f => f.ref.path) ?? [],
              selectedFixedColumns: table.fixed?.map(f => f.ref.path) ?? [],
              sortBy: table.sortBy?.path ?? 'none',
              sortOrder: table.sortOrder ?? EntityViewSortDirection.Asc,
            },
          });
        });

      builder.footerRight(new Command({ label: 'Cancel', action: () => ctx.onCancel() }));
      if (isEdit) builder.footerLeft(new Command({ label: 'Delete', action: () => ctx.onDelete() }));
      builder.footerSplitButton(new Command({
        label: isEdit ? 'Save Queue' : 'Create Queue',
        action: () => wizard.next(),
      }), []);

      builder
        .validator(validate)
        .validatorAsync(nameUnique(name => isQueueNameUnique(name, model._id)));

      const viewPaths = new Set([
        'list.columns',
        'list.sortBy',
        'list.sortOrder',
      ]);
      const requestPaths = new Set([
        'entityFilter',
        'list.sortBy',
        'list.sortOrder',
      ]);

      const form = builder.build();
      form.onUpdate((path, value) => {

        const previewValue = buildPreviewValue(form.value() as Record<string, unknown>, model);
        if (viewPaths.has(path)) ctx.onPreviewView(previewValue);
        if (requestPaths.has(path)) ctx.onPreviewRequest(previewValue);
      });
      const initial = buildPreviewValue(form.value() as Record<string, unknown>, model);
      ctx.onPreviewView(initial);
      ctx.onPreviewRequest(initial);

      return form;
    },
  };
}
